// @ts-nocheck: Legacy page using mock data. Will be rewritten to use real API services.
import { useState } from 'react';
import {
  DollarSign,
  ShoppingBag,
  Wallet,
  Building2,
  TrendingUp,
  Download,
} from 'lucide-react';
import { cn, formatCurrency } from '@lib/utils';
import {
  mockAnalyticsMetrics,
  mockTopPerformingMerchants,
  mockCategoryPerformance,
  mockRevenueData,
} from '@mocks/analytics.mock';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

type DateRange = 'last-7-days' | 'last-30-days' | 'last-90-days' | 'this-month' | 'last-month' | 'custom';
type ChartPeriod = 'daily' | 'weekly' | 'monthly';

// §17: charts are the ONE place Chekit allows non-green hues, because a
// single-hue palette cannot express a multi-series chart. #0891b2 (cyan) was
// a banned blue; the chart tokens replace the whole ramp.
const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

export const AnalyticsPage = () => {
  const [dateRange, setDateRange] = useState<DateRange>('last-7-days');
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('daily');

  const metrics = mockAnalyticsMetrics;
  const topMerchants = mockTopPerformingMerchants;
  const categoryPerformance = mockCategoryPerformance;

  /**
   * Format revenue data for display on chart
   */
  const formatRevenueChartData = () => {
    return mockRevenueData.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      revenue: item.revenue / 1000, // Convert to thousands for readability
      orders: item.orders,
    }));
  };

  /**
   * Format order distribution for pie chart
   */
  const formatOrderDistribution = () => {
    return categoryPerformance.map(cat => ({
      name: cat.category,
      value: cat.totalOrders,
      percentage: ((cat.totalOrders / metrics.totalOrders) * 100).toFixed(1),
    }));
  };

  /**
   * Custom tooltip for revenue chart
   */
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-2xl bg-white shadow-lift border border-[var(--border-subtle)] p-3 text-[13px]">
          <p className="font-semibold text-[var(--text-primary)]">{payload[0].payload.date}</p>
          <p className="text-[var(--text-accent)] font-medium mt-1">
            Revenue: {formatCurrency(payload[0].value * 1000)}
          </p>
          <p className="text-[var(--text-muted)]">Orders: {payload[0].payload.orders}</p>
        </div>
      );
    }
    return null;
  };

  /**
   * Custom tooltip for pie chart
   */
  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-2xl bg-white shadow-lift border border-[var(--border-subtle)] p-3 text-[13px]">
          <p className="font-semibold text-[var(--text-primary)]">{payload[0].name}</p>
          <p className="text-[var(--text-muted)] mt-1">
            Orders: {payload[0].value.toLocaleString()}
          </p>
          <p className="text-[var(--text-muted)]">{payload[0].payload.percentage}%</p>
        </div>
      );
    }
    return null;
  };

  /**
   * Returns rank badge classes for top-3 gold / silver / bronze treatment,
   * falling back to the neutral style for all other ranks.
   */
  const getRankBadgeClass = (rank: number): string => {
    if (rank === 1) return 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]';
    if (rank === 2) return 'bg-[var(--bg-base)] text-[var(--text-muted)]';
    if (rank === 3) return 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]';
    return 'bg-[var(--bg-base)] text-[var(--text-primary)]';
  };

  return (
    <div className="p-8 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Analytics Dashboard
          </h1>
          <p className="text-[14px] text-[var(--text-muted)] mt-1">Business intelligence and performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date Range Selector */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRange)}
            className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors cursor-pointer"
          >
            <option value="last-7-days">Last 7 Days</option>
            <option value="last-30-days">Last 30 Days</option>
            <option value="last-90-days">Last 90 Days</option>
            <option value="this-month">This Month</option>
            <option value="last-month">Last Month</option>
            <option value="custom">Custom Range</option>
          </select>

          {/* Export Button */}
          <button className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
            <Download className="w-4 h-4" strokeWidth={1.5} />
            Export Report
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Revenue */}
        <div className="rounded-3xl bg-white shadow-soft p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[13px] text-[var(--text-muted)] mt-1">Total Revenue</span>
            <div className="w-10 h-10 rounded-2xl bg-[var(--bg-base)] flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
            </div>
          </div>
          <div className="font-display text-[36px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mb-2 leading-none">
            {formatCurrency(metrics.totalRevenue)}
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-[var(--state-success)]" strokeWidth={1.5} />
            <span className="text-[12px] font-semibold text-[var(--state-success)]">
              +{metrics.revenueGrowth}% from last period
            </span>
          </div>
        </div>

        {/* Total Orders */}
        <div className="rounded-3xl bg-white shadow-soft p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[13px] text-[var(--text-muted)] mt-1">Total Orders</span>
            <div className="w-10 h-10 rounded-2xl bg-[var(--bg-base)] flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-[var(--state-warning)]" strokeWidth={1.5} />
            </div>
          </div>
          <div className="font-display text-[36px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mb-2 leading-none">
            {metrics.totalOrders.toLocaleString()}
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-[var(--state-success)]" strokeWidth={1.5} />
            <span className="text-[12px] font-semibold text-[var(--state-success)]">
              +{metrics.ordersGrowth}% from last period
            </span>
          </div>
        </div>

        {/* Platform Commission */}
        <div className="rounded-3xl bg-white shadow-soft p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[13px] text-[var(--text-muted)] mt-1">Platform Commission</span>
            <div className="w-10 h-10 rounded-2xl bg-[var(--bg-base)] flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
            </div>
          </div>
          <div className="font-display text-[36px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mb-2 leading-none">
            {formatCurrency(metrics.platformCommission)}
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-[var(--state-success)]" strokeWidth={1.5} />
            <span className="text-[12px] font-semibold text-[var(--state-success)]">
              +{metrics.commissionGrowth}% from last period
            </span>
          </div>
        </div>

        {/* Active Merchants */}
        <div className="rounded-3xl bg-white shadow-soft p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[13px] text-[var(--text-muted)] mt-1">Active Merchants</span>
            <div className="w-10 h-10 rounded-2xl bg-[var(--bg-base)] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
            </div>
          </div>
          <div className="font-display text-[36px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mb-2 leading-none">
            {metrics.activeMerchants}
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-[var(--state-success)]" strokeWidth={1.5} />
            <span className="text-[12px] font-semibold text-[var(--state-success)]">
              +{metrics.merchantsGrowth} new this period
            </span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Trends Chart */}
        <div className="lg:col-span-2 rounded-3xl bg-white shadow-soft p-6">
          <div className="flex items-center justify-between pb-4 border-b border-[var(--border-subtle)] mb-6">
            <div>
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">Revenue Trends</h2>
              <p className="text-[13px] text-[var(--text-muted)] mt-0.5">Platform revenue over time</p>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setChartPeriod('daily')}
                className={cn(
                  'px-3 py-1 text-[13px] font-semibold rounded-full transition-all',
                  chartPeriod === 'daily'
                    ? 'bg-[var(--brand-forest)] text-white'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-base)]'
                )}
              >
                Daily
              </button>
              <button
                onClick={() => setChartPeriod('weekly')}
                className={cn(
                  'px-3 py-1 text-[13px] font-semibold rounded-full transition-all',
                  chartPeriod === 'weekly'
                    ? 'bg-[var(--brand-forest)] text-white'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-base)]'
                )}
              >
                Weekly
              </button>
              <button
                onClick={() => setChartPeriod('monthly')}
                className={cn(
                  'px-3 py-1 text-[13px] font-semibold rounded-full transition-all',
                  chartPeriod === 'monthly'
                    ? 'bg-[var(--brand-forest)] text-white'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-base)]'
                )}
              >
                Monthly
              </button>
            </div>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={formatRevenueChartData()}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
                <XAxis
                  dataKey="date"
                  stroke="var(--text-muted)"
                  tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                />
                <YAxis
                  stroke="var(--text-muted)"
                  tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                  tickFormatter={(value) => `${value}K`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="var(--chart-1)"
                  strokeWidth={3}
                  fill="url(#revenueGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Order Distribution Chart */}
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <div className="pb-4 border-b border-[var(--border-subtle)] mb-6">
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">Order Distribution</h2>
            <p className="text-[13px] text-[var(--text-muted)] mt-0.5">Orders by category</p>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={formatOrderDistribution()}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {formatOrderDistribution().map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {formatOrderDistribution().map((item, index) => (
              <div key={item.name} className="flex items-center justify-between text-[12px]">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  <span className="text-[var(--text-secondary)]">{item.name}</span>
                </div>
                <span className="font-semibold text-[var(--text-primary)]">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Performing Merchants Table */}
      <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
        <div className="px-6 py-5 border-b border-[var(--border-subtle)]">
          <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">Top Performing Merchants</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-[var(--border-subtle)] bg-[var(--bg-base)]">
              <tr>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Rank
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Merchant
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Category
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Total Orders
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Revenue
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Commission Earned
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody>
              {topMerchants.map((merchant) => (
                <tr
                  key={merchant.id}
                  className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-base)] transition-colors duration-150 cursor-pointer"
                >
                  <td className="px-6 py-4 text-[14px] text-[var(--text-secondary)]">
                    <div className={cn(
                      'rounded-full text-[12px] font-semibold w-7 h-7 flex items-center justify-center',
                      getRankBadgeClass(merchant.rank)
                    )}>
                      {merchant.rank}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-2xl bg-[var(--bg-elevated)] text-[var(--text-accent)] font-semibold text-[13px] flex items-center justify-center">
                        {merchant.initials}
                      </div>
                      <span className="text-[14px] font-semibold text-[var(--text-primary)]">{merchant.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[12px] text-[var(--text-muted)]">
                    {merchant.category}
                  </td>
                  <td className="px-6 py-4 text-[14px] text-[var(--text-secondary)]">
                    {merchant.totalOrders.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-[14px] font-semibold text-[var(--text-primary)]">
                    {formatCurrency(merchant.revenue)}
                  </td>
                  <td className="px-6 py-4 text-[14px] text-[var(--state-success)]">
                    {formatCurrency(merchant.commission)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="w-[150px]">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[14px] font-semibold text-[var(--text-primary)]">
                          {merchant.performanceScore}%
                        </span>
                      </div>
                      <div className="w-full rounded-full h-1.5 bg-[var(--bg-elevated)] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[var(--gradient-to)] transition-all"
                          style={{ width: `${merchant.performanceScore}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance by Category Table */}
      <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
        <div className="px-6 py-5 border-b border-[var(--border-subtle)]">
          <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">Performance by Category</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-[var(--border-subtle)] bg-[var(--bg-base)]">
              <tr>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Category
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Total Orders
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Revenue
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Avg Order Value
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Commission Rate
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Commission Earned
                </th>
              </tr>
            </thead>
            <tbody>
              {categoryPerformance.map((category) => (
                <tr
                  key={category.category}
                  className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-base)] transition-colors duration-150 cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <span className="text-[14px] font-semibold text-[var(--text-primary)]">
                      {category.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[14px] text-[var(--text-secondary)]">
                    {category.totalOrders.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-[14px] text-[var(--text-secondary)]">
                    {formatCurrency(category.revenue)}
                  </td>
                  <td className="px-6 py-4 text-[14px] text-[var(--text-secondary)]">
                    {formatCurrency(category.avgOrderValue)}
                  </td>
                  <td className="px-6 py-4">
                    <span className="rounded-full bg-[var(--bg-elevated)] text-[var(--text-accent)] text-[12px] font-semibold px-2.5 py-0.5">
                      {category.commissionRate}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[14px] text-[var(--text-secondary)]">
                    {formatCurrency(category.commissionEarned)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
